import { DatadogTrack } from '@cobliteam/datadog-rum';
import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { getAuthHeaders } from '../account/session/AuthToken';
import { getOpenTelemetryBaggageHttpHeaders } from '../openTelemetryBaggage';
import HttpMethod from './HttpMethod';
import HttpStatusCode from './HttpStatusCode';
import TimeZone from './Timezone';
import type { Configuration, IRequest } from './Types';

export class CobliAxios {
  private axiosInstance: AxiosInstance;

  private configBaseURL: string;

  constructor(configuration: Configuration) {
    this.axiosInstance = axios.create();
    this.configBaseURL = configuration.host;
  }

  public request = (requestData: IRequest, signal?: AbortSignal, nonReportableToDatadogErrors: HttpStatusCode[] = [], external = false) => {
    const cobliHeaders = {
      ...getAuthHeaders(),
      'cobli-timezone': TimeZone.getTimezone(),
      ...getOpenTelemetryBaggageHttpHeaders(),
    };

    const headers: Record<string, string> = external
      ? { 'Content-type': 'application/json', ...requestData.headers }
      : {
          ...cobliHeaders,
          'Content-type': 'application/json',
          ...requestData.headers,
        };

    return this.axiosInstance
      .request({
        url: requestData.endpoint,
        baseURL: requestData.externalBaseURL || this.configBaseURL,
        method: HttpMethod[requestData.method],
        headers,
        data: requestData.body,
        params: requestData.params,
        /*
      The default responseType is json, so it's not need to pass.
      See more in https://github.com/axios/axios#request-config
      */
        responseType: requestData.responseType,
        signal,

        /*
      We need withCredential to make browser accept and send security headers.
      i.e. Cookie/Set-Cookie
      */
        withCredentials: requestData.withCredentials,
      })
      .catch((error) => {
        if (error.response && !nonReportableToDatadogErrors.includes(error.response.status)) {
          this.datadogCatch(requestData, error);
        }

        throw error;
      });
  };

  private datadogCatch = (requestData: IRequest, error: AxiosError) => {
    const errorInfo = this.buildErrorInfo(requestData, error);
    const errorMessage = this.buildErrorMessage(requestData, error);

    // Create a more descriptive error object for Datadog
    const datadogError = new Error(errorMessage);
    datadogError.name = 'CobliAxiosError';

    DatadogTrack.error(datadogError, {
      ...errorInfo,
      EventSource: 'CobliAxios',
    });

    if (error.response) {
      switch (error.response.status) {
        case HttpStatusCode.UNAUTHORIZED:
          // TODO: use fancy history object instead to help mocking and stuff
          window.location.reload();
      }
    }
  };

  private buildErrorInfo = (requestData: IRequest, error: AxiosError): Record<string, any> => {
    const errorInfo: Record<string, any> = {
      // Request context
      endpoint: requestData.endpoint,
      method: HttpMethod[requestData.method],
      baseURL: requestData.externalBaseURL || this.configBaseURL,
      fullURL: error.config?.url,
      external: !!requestData.externalBaseURL,

      // Error details
      errorType: this.getErrorType(error),
      errorCode: error.code,
      originalMessage: error.message,
    };

    // Response details
    if (error.response) {
      errorInfo.status = error.response.status;
      errorInfo.statusText = error.response.statusText;
      errorInfo.responseHeaders = error.response.headers;

      // Try to extract meaningful response data
      if (error.response.data) {
        try {
          const responseData = typeof error.response.data === 'string' ? JSON.parse(error.response.data) : error.response.data;

          errorInfo.responseData = {
            message: responseData.message || responseData.error || responseData.detail,
            code: responseData.code || responseData.errorCode,
            details: responseData.details || responseData.errors,
          };
        } catch {
          // If parsing fails, include raw data (truncated)
          errorInfo.responseData = {
            raw: typeof error.response.data === 'string' ? error.response.data.substring(0, 500) : String(error.response.data).substring(0, 500),
          };
        }
      }
    }

    // Request details (for debugging)
    if (requestData.params && Object.keys(requestData.params).length > 0) {
      errorInfo.requestParams = requestData.params;
    }

    if (requestData.body) {
      errorInfo.hasRequestBody = true;
      errorInfo.requestBodyType = typeof requestData.body;
    }

    // Network error details
    if (error.request && !error.response) {
      errorInfo.networkError = true;
      errorInfo.requestSent = !!error.request;
    }

    return errorInfo;
  };

  private buildErrorMessage = (requestData: IRequest, error: AxiosError): string => {
    const method = HttpMethod[requestData.method];
    const endpoint = requestData.endpoint;
    const baseURL = requestData.externalBaseURL || this.configBaseURL;
    const fullURL = `${baseURL}${endpoint}`;

    if (error.response) {
      const status = error.response.status;
      const statusText = error.response.statusText;

      // Try to get meaningful error message from response
      let responseMessage = '';
      if (error.response.data) {
        try {
          const responseData = typeof error.response.data === 'string' ? JSON.parse(error.response.data) : error.response.data;

          responseMessage = responseData.message || responseData.error || responseData.detail || '';
        } catch {
          // If parsing fails, use status text
          responseMessage = statusText;
        }
      }

      const statusDescription = this.getStatusDescription(status);
      const message = responseMessage ? `${statusDescription} (${status} ${statusText}): ${responseMessage}` : `${statusDescription} (${status} ${statusText})`;

      return `HTTP ${method} ${fullURL} failed - ${message}`;
    }

    if (error.request && !error.response) {
      return `Network error for ${method} ${fullURL} - ${error.message}`;
    }

    return `Request failed for ${method} ${fullURL} - ${error.message}`;
  };

  private getErrorType = (error: AxiosError): string => {
    if (error.response) {
      const status = error.response.status;
      if (status >= 500) return 'SERVER_ERROR';
      if (status >= 400) return 'CLIENT_ERROR';
      if (status >= 300) return 'REDIRECT_ERROR';
      return 'HTTP_ERROR';
    }

    if (error.request && !error.response) return 'NETWORK_ERROR';
    if (error.code === 'ECONNABORTED') return 'TIMEOUT_ERROR';
    if (error.code === 'ERR_CANCELED') return 'CANCELLED_ERROR';

    return 'UNKNOWN_ERROR';
  };

  private getStatusDescription = (status: number): string => {
    switch (status) {
      case 400:
        return 'Bad Request';
      case 401:
        return 'Unauthorized';
      case 403:
        return 'Forbidden';
      case 404:
        return 'Not Found';
      case 405:
        return 'Method Not Allowed';
      case 408:
        return 'Request Timeout';
      case 409:
        return 'Conflict';
      case 422:
        return 'Unprocessable Entity';
      case 429:
        return 'Too Many Requests';
      case 500:
        return 'Internal Server Error';
      case 502:
        return 'Bad Gateway';
      case 503:
        return 'Service Unavailable';
      case 504:
        return 'Gateway Timeout';
      default:
        return 'HTTP Error';
    }
  };
}
